import { Link, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { getArticleBySlug } from '@/mocks/articleData';
import type { ArticleSection } from '@/mocks/articleData';
import SeoSchema from '@/components/base/SeoSchema';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">Article Not Found</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 underline text-sm">Back to Home</Link>
        </div>
      </div>
    );
  }



  const renderSection = (section: ArticleSection, idx: number) => {
    switch (section.type) {
      case 'heading':
        return (
          <h2 key={idx} className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
            {section.content}
          </h2>
        );
      case 'paragraph':
        return (
          <p key={idx} className="text-sm md:text-base text-foreground-600 leading-relaxed mb-3">
            {section.content}
          </p>
        );
      case 'list':
        return (
          <ul key={idx} className="space-y-2 ml-5 mb-4">
            {section.items?.map((item, i) => {
              const parts = item.split(':');
              const label = parts[0];
              const rest = parts.slice(1).join(':');
              return (
                <li key={i} className="flex items-start gap-2 text-sm md:text-base text-foreground-600">
                  <i className="ri-checkbox-circle-fill text-primary-500 mt-0.5 flex-shrink-0"></i>
                  <span><strong>{label}:</strong>{rest}</span>
                </li>
              );
            })}
          </ul>
        );
      case 'categories':
        return (
          <div key={idx} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            {article.categoryLinks.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="block p-4 bg-background-100 border border-background-200/60 rounded-xl hover:border-primary-200/60 hover:bg-primary-50/20 transition-all group"
              >
                <h3 className="font-heading font-semibold text-sm text-foreground-900 mb-1.5 group-hover:text-primary-600 transition-colors">
                  {cat.name}
                </h3>
                <p className="text-xs text-foreground-500 leading-relaxed">Click to explore the {cat.name} category on the main Vigorbuy Spreadsheet platform.</p>
              </a>
            ))}
          </div>
        );
      case 'tips':
        return (
          <div key={idx} className="space-y-4 mb-4">
            {section.tipItems?.map((tip, i) => (
              <div key={i} className="flex gap-3">
                <div className="w-6 h-6 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-accent-600">{i + 1}</span>
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-sm md:text-base text-foreground-900 mb-1">{tip.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{tip.desc}</p>
                </div>
              </div>
            ))}
          </div>
        );
      case 'comparison':
        return (
          <div key={idx} className="overflow-x-auto mb-4">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-background-200/70">
                  <th className="text-left py-3 px-4 font-semibold text-foreground-700 whitespace-nowrap">Aspect</th>
                  <th className="text-center py-3 px-4 font-semibold text-primary-600 whitespace-nowrap">Vigorbuy Spreadsheet</th>
                  <th className="text-center py-3 px-4 font-semibold text-foreground-400 whitespace-nowrap">Traditional Shopping</th>
                </tr>
              </thead>
              <tbody>
                {section.comparisonRows?.map((row, i) => (
                  <tr key={i} className="border-b border-background-200/40">
                    <td className="py-3 px-4 font-medium text-foreground-800 whitespace-nowrap">{row.label}</td>
                    <td className="py-3 px-4 text-center text-primary-600 font-medium whitespace-nowrap">{row.vigorbuy}</td>
                    <td className="py-3 px-4 text-center text-foreground-400 whitespace-nowrap">{row.traditional}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case 'cta':
        return (
          <div key={idx} className="flex flex-col sm:flex-row gap-3 mt-6 mb-4">
            {section.buttons?.map((btn, i) =>
              btn.isExternal ? (
                <a
                  key={i}
                  href={btn.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className={`inline-flex items-center justify-center gap-2 px-7 py-3.5 font-semibold text-sm rounded-full transition-all whitespace-nowrap ${
                    btn.isPrimary
                      ? 'bg-primary-500 hover:bg-primary-600 text-background-50'
                      : 'bg-background-100 border border-background-200 hover:bg-background-200/70 text-foreground-700'
                  }`}
                >
                  {btn.label}
                  {btn.isExternal && <i className="ri-arrow-right-up-line"></i>}
                </a>
              ) : (
                <Link
                  key={i}
                  to={btn.url}
                  className={`inline-flex items-center justify-center gap-2 px-7 py-3.5 font-semibold text-sm rounded-full transition-all whitespace-nowrap ${
                    btn.isPrimary
                      ? 'bg-primary-500 hover:bg-primary-600 text-background-50'
                      : 'bg-background-100 border border-background-200 hover:bg-background-200/70 text-foreground-700'
                  }`}
                >
                  {btn.label}
                </Link>
              )
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SeoSchema type="article" article={article} />

      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/60">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-table-line text-background-50 text-lg"></i>
            </div>
            <span className="font-heading font-bold text-lg text-foreground-900 whitespace-nowrap">
              Vigorbuy<span className="text-primary-500">Spread</span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="px-4 py-2 text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap"
            >
              ← Back to Home
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 text-sm font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-all whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>
        </div>
      </nav>

      <article className="max-w-[860px] mx-auto px-4 md:px-6 py-10 md:py-14">
        <header className="mb-10">
          <div className="flex items-center gap-2 text-xs text-foreground-400 mb-4">
            <Link to="/" className="hover:text-primary-500 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line text-[10px]"></i>
            <span className="text-foreground-500">{article.title}</span>
          </div>
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-tight mb-4">
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-400">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              Updated: {new Date(article.dateModified).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line"></i>
              {article.readTime}
            </span>
            <span className="px-2 py-0.5 bg-primary-50 text-primary-600 rounded-full text-[11px] font-medium">
              {article.badge}
            </span>
          </div>
        </header>

        <img
          src={article.imageUrl}
          alt={article.imageAlt}
          title={article.title}
          className="w-full rounded-2xl mb-10"
        />

        <div className="prose-custom">
          {article.sections.map((section, idx) => (
            <section key={idx} className="mb-10">
              {renderSection(section, idx)}
            </section>
          ))}

          <section className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {article.faqs.map((faq, i) => (
                <div key={i}>
                  <h3 className="font-heading font-semibold text-sm md:text-base text-foreground-900 mb-1.5">
                    {faq.q}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
              Final Thoughts
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
              We hope this guide has given you a solid foundation for using <strong>Vigorbuy Spreadsheet</strong> effectively. The platform continues to evolve with new product categories, updated listings and improved comparison tools — making it an increasingly valuable resource for smart shoppers everywhere.
            </p>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
              Remember, the key to successful shopping with Vigorbuy Spreadsheet is consistent research, careful QC photo analysis, and patience in finding the right product at the right price. The spreadsheet format rewards methodical comparison, and the more you use it, the faster and more intuitive your research process becomes.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold text-sm rounded-full transition-all whitespace-nowrap"
              >
                Explore Vigorbuy Spreadsheet
                <i className="ri-arrow-right-up-line"></i>
              </a>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-background-100 border border-background-200 hover:bg-background-200/70 text-foreground-700 font-medium text-sm rounded-full transition-all whitespace-nowrap"
              >
                ← Back to Resource Hub
              </Link>
            </div>
          </section>

          <div className="mt-12 pt-8 border-t border-background-200/60">
            <h3 className="font-heading font-semibold text-sm text-foreground-500 mb-4">
              Explore More Resources
            </h3>
            <div className="flex flex-wrap gap-3">
              <Link to="/" className="px-4 py-2 bg-background-100 border border-background-200/60 rounded-full text-xs font-medium text-foreground-600 hover:text-primary-500 hover:border-primary-200/60 transition-all whitespace-nowrap">
                Home
              </Link>
              {article.relatedLinks.map((link) =>
                link.isExternal ? (
                  <a key={link.label} href={link.url} target="_blank" rel="noopener nofollow" className="px-4 py-2 bg-background-100 border border-background-200/60 rounded-full text-xs font-medium text-foreground-600 hover:text-primary-500 hover:border-primary-200/60 transition-all whitespace-nowrap">
                    {link.label}
                  </a>
                ) : (
                  <Link key={link.label} to={link.url} className="px-4 py-2 bg-background-100 border border-background-200/60 rounded-full text-xs font-medium text-foreground-600 hover:text-primary-500 hover:border-primary-200/60 transition-all whitespace-nowrap">
                    {link.label}
                  </Link>
                )
              )}
            </div>
          </div>
        </div>
      </article>

      <footer className="bg-secondary-50 border-t border-background-200/60">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-primary-500 flex items-center justify-center">
                <i className="ri-table-line text-background-50 text-sm"></i>
              </div>
              <span className="font-heading font-bold text-sm text-foreground-900">Vigorbuy<span className="text-primary-500">Spread</span></span>
            </div>
            <p className="text-xs text-foreground-400">&copy; 2026 Vigorbuy Spreadsheet. All rights reserved.</p>
            <Link to="/" className="text-xs text-foreground-400 hover:text-primary-500 transition-colors whitespace-nowrap">← Back to Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}